import { formatDateIn, formatMoneyIn, type Locale } from '@zuruflex/i18n';
import type { Booking, Promotion } from '@zuruflex/types';
import type { TFunction } from 'i18next';

/**
 * What a promo takes off, as a renter would read it: "10% off", "GHS 50
 * off". The same `common:promo.badge.*` keys the car cards wear, so the
 * portal and the website describe one promo in one sentence.
 */
export function promoValue(
  promo: Pick<Promotion, 'discountType' | 'percentBps' | 'amountOff' | 'currency'>,
  t: TFunction,
  locale: Locale,
): string {
  return promo.discountType === 'PERCENT'
    ? t('common:promo.badge.percent', { percent: (promo.percentBps ?? 0) / 100 })
    : t('common:promo.badge.amount', {
        amount: formatMoneyIn(promo.amountOff ?? 0, promo.currency, locale),
      });
}

/**
 * Where a promo is in its life, which is more than its status says: an
 * ACTIVE promo whose window has not opened, or has closed, applies to
 * nothing, and a list that calls it "Live" is a promise the checkout does
 * not keep.
 */
export type PromoPhase = Promotion['status'] | 'scheduled' | 'ended';

export function promoPhase(promo: Promotion, now = Date.now()): PromoPhase {
  if (promo.status !== 'ACTIVE') return promo.status;
  if (new Date(promo.startsAt).getTime() > now) return 'scheduled';
  if (promo.endsAt && new Date(promo.endsAt).getTime() <= now) return 'ended';
  return 'ACTIVE';
}

export const PHASE_VARIANT = {
  ACTIVE: 'success',
  PAUSED: 'warning',
  ARCHIVED: 'outline',
  scheduled: 'info',
  ended: 'default',
} as const;

const DAY_MS = 86_400_000;

/**
 * `endsAt` is exclusive — the first instant it no longer applies — but a
 * person picks the last day it *does*. These two convert between them, at
 * local midnight, so "last day: Sunday" runs to the end of Sunday.
 */
export function lastDayOf(endsAt: string): Date {
  return new Date(new Date(endsAt).getTime() - DAY_MS);
}

export function endsAtAfter(lastDay: Date): string {
  const next = new Date(lastDay);
  next.setHours(0, 0, 0, 0);
  next.setDate(next.getDate() + 1);
  return next.toISOString();
}

/** "From 3 Mar" or "3 Mar – 30 Apr", with the end shown as the last day. */
export function promoWindow(promo: Promotion, t: TFunction, locale: Locale): string {
  const from = formatDateIn(promo.startsAt, locale);
  return promo.endsAt
    ? t('partner:promotions.runsBetween', {
        from,
        to: formatDateIn(lastDayOf(promo.endsAt), locale),
      })
    : t('partner:promotions.runsFrom', { from });
}

/**
 * A hire's money as the partner is paid it, promo included.
 *
 * `subtotal` is already after the discount, so on its own it hides who
 * paid for the discount. Working back from the snapshot:
 *
 * - `gross` is the price before any promo.
 * - A partner-funded promo is the partner's cost, shown as its own line.
 * - `commission` is whatever else stands between gross and earnings. On a
 *   Zuruflex-funded promo that is Zuruflex's cut of the full price — the booking's
 *   own `commissionAmount` is smaller by the discount, and can be negative,
 *   which is Zuruflex's arithmetic and not the partner's.
 *
 * gross − partnerPromo − commission = partnerEarnings, always.
 */
export interface Settlement {
  gross: number;
  promo: { amount: number; funder: 'ZURUFLEX' | 'PARTNER'; code: string | null } | null;
  partnerPromo: number;
  commission: number;
}

export function settlementOf(booking: Booking): Settlement {
  const discount = booking.promoDiscount ?? 0;
  const funder = booking.promoFunder ?? null;
  const promo =
    discount > 0 && funder ? { amount: discount, funder, code: booking.promoCode ?? null } : null;
  const gross = booking.subtotal + (promo ? discount : 0);
  const partnerPromo = promo?.funder === 'PARTNER' ? discount : 0;
  return {
    gross,
    promo,
    partnerPromo,
    commission: gross - partnerPromo - booking.partnerEarnings,
  };
}

/** "Promo WEEKEND10", or "Automatic promo" for one nobody typed. */
export function promoLabel(code: string | null, t: TFunction): string {
  return code ? t('common:priceLine.promo', { code }) : t('partner:bookings.promoAutomatic');
}
